/**
 * billToSourceTag.ts — R4 (SPEC invoice-identity-cascade) — AC1 / AC5 / AC9.
 *
 * The plain-language SOURCE TAG every BILL TO row shows, derived from R2's
 * resolution bundle (models/invoice_identity_resolution.py → ResolutionSource +
 * the per-field `resolved`/`suggested` flags). This is the single source of
 * truth for "where did this value come from, and does it carry?" so the panel,
 * the inline picker, and the gate all read the SAME copy.
 *
 * The Christa-psychology bar (R4 Cold-Reader Context):
 *   - ⚠ / amber is RESERVED for the genuinely-BLOCKING `needs a client` state
 *     ONLY (consistent with the existing CRIT-4/2a polish that demoted amber
 *     chips to slate). A Tier-2 cross-project value is NOT a warning — it
 *     renders CONFIDENT + non-alarming ("✓ from {client}'s last invoice {#} —
 *     confirm?"), a yes/no, not a red flag.
 *   - Invoice numbers are her native language; they are shown VERBATIM,
 *     zero-padded, exactly as stored (no re-derivation).
 *   - NO user-facing string here may contain "scope" / "establishment" /
 *     "field_update_mode" — AC1 string audit. We use project name / client
 *     name / "DBA". `assertNoSystemVocab` is the runtime guard a test runs over
 *     every produced tag.
 *
 * The `tone` a tag carries:
 *   - 'quiet'   → a confidently-resolved value from a stored default. The panel
 *                 SUPPRESSES the caption in the dominant happy case (AC9 +
 *                 CRIT-4/2c density polish) — the value alone is enough.
 *   - 'confirm' → a Tier-2 cross-project SUGGESTION. Shown, non-alarming, asks
 *                 for a yes. NEVER amber.
 *   - 'blocking'→ the unresolved `needs a client` state. This is the ONLY amber
 *                 tag; it gates send (AC6).
 */

/** R2 ResolutionSource, mirrored on the FE (string-equal to the backend enum). */
export type ResolutionSource =
  | 'project_default'
  | 'client_default'
  | 'last_sent_tier1'
  | 'last_sent_tier2'
  | 'unresolved';

/** One resolved cascade field as R2 returns it (ResolvedField.to_dict()). */
export interface ResolvedFieldDto {
  /** The resolved value, or the literal "unresolved" sentinel. */
  value: unknown;
  source: ResolutionSource;
  suggested: boolean;
  resolved: boolean;
}

export type SourceTagTone = 'quiet' | 'confirm' | 'blocking';

export interface SourceTag {
  /** The plain-language caption, e.g. "· project default" or "⚠ needs a client". */
  text: string;
  tone: SourceTagTone;
  /** True only for the blocking `needs a client` state — the gate reads this. */
  blocking: boolean;
  /** True for the Tier-2 confirm state — drives the confident (non-amber) chip. */
  confirm: boolean;
  /**
   * Whether the panel should SHOW this caption by default. Quiet/resolved
   * stored-default tags are suppressed in the dominant case (AC9 / CRIT-4/2c);
   * confirm + blocking tags always show because they carry override-relevant
   * signal.
   */
  show: boolean;
}

/**
 * System vocabulary that must NEVER appear in user-facing copy (AC1).
 *
 * R3 (invoice-identity-correctable): 'propagate' joins the always-forbidden
 * set. It's system vocabulary that never appears in Christa-facing copy anyway
 * (the component is named PropagationModal, but that identifier is not rendered
 * text). Adding it makes the audit guard catch any future leak.
 *
 * NOTE 'default' is deliberately NOT in the always-forbidden set: it appears in
 * legitimate provenance captions ("· project default" describes where a value
 * came from, not the persistence choice). R3 forbids 'default' ONLY inside the
 * BINARY PERSISTENCE PICKER copy (the choice must be in her words, never "set as
 * default"); callers opt into that stricter rule via `extraForbidden`.
 */
const FORBIDDEN_VOCAB = ['scope', 'establishment', 'field_update_mode', 'propagate'] as const;

/**
 * AC1 string-audit guard. Throws if a produced user-facing string leaks system
 * vocabulary. Case-insensitive, whole-word-ish (substring is intentional — even
 * "establishments" must not appear). A test runs this over every code path.
 *
 * `extraForbidden` lets a caller (the binary persistence picker) apply the
 * stricter R3 AC1 list that ALSO bans 'default' inside the persistence choice.
 */
export function assertNoSystemVocab(
  text: string,
  context = 'bill-to copy',
  extraForbidden: readonly string[] = [],
): void {
  const lower = text.toLowerCase();
  for (const bad of [...FORBIDDEN_VOCAB, ...extraForbidden]) {
    if (lower.includes(bad.toLowerCase())) {
      throw new Error(
        `Forbidden system vocabulary "${bad}" leaked into ${context}: "${text}"`,
      );
    }
  }
}

/**
 * The stricter forbidden list for the BINARY PERSISTENCE PICKER (R3 AC1). The
 * persistence choice must read in Christa's words only — never "scope",
 * "establishment", "default", "field_update_mode", or "propagate".
 */
export const PERSISTENCE_CHOICE_FORBIDDEN = ['default'] as const;

/**
 * Build the source tag for a resolved cascade field.
 *
 * @param field    the R2 ResolvedField dto (or undefined → treated as quiet/no-tag)
 * @param opts.clientName     the client's display name (Tier-2 confirm copy)
 * @param opts.lastSentNumber the zero-padded invoice number the value seeds from
 *                            (Tier-1 / Tier-2 provenance — shown VERBATIM)
 * @param opts.needsClient    when true, the field is unresolved BECAUSE the
 *                            invoice has no client yet → the blocking state.
 */
export function buildSourceTag(
  field: ResolvedFieldDto | null | undefined,
  opts: {
    clientName?: string | null;
    lastSentNumber?: string | null;
    needsClient?: boolean;
  } = {},
): SourceTag {
  const { clientName, lastSentNumber, needsClient } = opts;

  // Unresolved → the ONLY amber/blocking state, and ONLY when it's the
  // genuinely-blocking "needs a client" case. (An unresolved field on an
  // invoice that DOES have a client is a fillable blank, not a red flag — it
  // renders quiet and the picker default carries it forward.)
  if (!field || field.source === 'unresolved' || !field.resolved) {
    if (needsClient) {
      const tag: SourceTag = {
        text: '⚠ needs a client',
        tone: 'blocking',
        blocking: true,
        confirm: false,
        show: true,
      };
      assertNoSystemVocab(tag.text);
      return tag;
    }
    // Unresolved-but-not-blocking: a quiet, suppressible blank. No amber.
    const tag: SourceTag = {
      text: '',
      tone: 'quiet',
      blocking: false,
      confirm: false,
      show: false,
    };
    return tag;
  }

  // Tier-2 cross-project SUGGESTION → confident, non-alarming, asks for a yes.
  // ✓ (not ⚠). This is the 560/561 case the red-team flagged as the one most
  // likely to be misread as "broken".
  if (field.source === 'last_sent_tier2') {
    const num = formatInvoiceNumber(lastSentNumber);
    const who = clientName?.trim() || 'this client';
    const text = num
      ? `✓ from ${who}'s last invoice ${num} — confirm?`
      : `✓ from ${who}'s last invoice — confirm?`;
    const tag: SourceTag = {
      text,
      tone: 'confirm',
      blocking: false,
      confirm: true,
      show: true,
    };
    assertNoSystemVocab(tag.text);
    return tag;
  }

  // Tier-1 last-sent on this exact project + client → provenance by number.
  if (field.source === 'last_sent_tier1') {
    const num = formatInvoiceNumber(lastSentNumber);
    const text = num ? `· from ${num}` : '· from last invoice';
    const tag: SourceTag = {
      text,
      tone: 'quiet',
      blocking: false,
      confirm: false,
      // Provenance from a prior invoice carries override-relevant signal
      // (she may want to confirm it matches) — show it.
      show: true,
    };
    assertNoSystemVocab(tag.text);
    return tag;
  }

  // Stored project default — the dominant happy case. Quiet + suppressible.
  if (field.source === 'project_default') {
    const tag: SourceTag = {
      text: '· project default',
      tone: 'quiet',
      blocking: false,
      confirm: false,
      show: false,
    };
    assertNoSystemVocab(tag.text);
    return tag;
  }

  // Stored client default — quiet, suppressible.
  const tag: SourceTag = {
    text: '· client default',
    tone: 'quiet',
    blocking: false,
    confirm: false,
    show: false,
  };
  assertNoSystemVocab(tag.text);
  return tag;
}

/**
 * Echo a stored invoice number VERBATIM (zero-padding preserved). We bracket it
 * the way Christa writes it ("[0586]") only at call sites that render a list;
 * the tag itself keeps the raw number so the bracketing stays a render concern.
 * Returns '' for a missing number so callers fall back to generic copy.
 */
export function formatInvoiceNumber(num: string | null | undefined): string {
  if (num === null || num === undefined) return '';
  const t = String(num).trim();
  return t;
}
